/**
 * Utility functions for building JSONPath expressions for the EditableFieldWithWarnings component
 */

import { Box, Typography } from "@mui/material";
import { JSONPath } from 'jsonpath-plus';
import EditableFieldWithWarnings from './EditableFieldWithWarnings.js';

/**
 * Builds JSONPath to the needs_review/caveats array entry by field_name (new schema).
 * Returns path to the full entry (not .reason) so that clearing removes the entire entry.
 * @param {string} basePath - JSONPath prefix
 * @param {string} fieldName - The field_name to match in the array
 * @param {'needs_review'|'caveats'} type
 * @returns {string} Full JSONPath (e.g. "$.path.needs_review[?(@.field_name=='providers')]")
 */
export const buildArraySchemaWarningPath = (basePath, fieldName, type) =>
  `${basePath}.${type}[?(@.field_name=='${fieldName}')]`;

/**
 * Builds JSONPath expressions for regular fields in medical data
 * @param {string} documentName - Name of the document
 * @param {string} dataType - Type of data ('medical_chronology' or 'medical_expenses')
 * @param {number} itemIndex - Index of the item in the array
 * @param {string} fieldName - Name of the field
 * @returns {Object} Object containing textPath, needsReviewPath, and caveatsPath
 */
export const buildFieldPaths = (documentName, dataType, itemIndex, fieldName) => {
  const base = `$.documents['${documentName}'].analysis.${dataType}[${itemIndex}]`;
  return {
    textPath: `${base}.${fieldName}`,
    needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
    caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
  };
};

/**
 * Builds JSONPath expressions for source sub-fields in medical data
 * @param {string} documentName - Name of the document
 * @param {string} dataType - Type of data ('medical_chronology' or 'medical_expenses')
 * @param {number} itemIndex - Index of the item in the array
 * @param {string} fieldName - Name of the field
 * @returns {Object} Object containing textPath, needsReviewPath, and caveatsPath
 */
export const buildFieldSourcePaths = (documentName, dataType, itemIndex, fieldName) => {
  const base = `$.documents['${documentName}'].analysis.${dataType}[${itemIndex}].source`;
  return {
    textPath: `${base}.${fieldName}`,
    needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
    caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
  };
};

/**
 * Helper function to get the user's locale
 * @returns {string} The user's locale (e.g., 'en-US', 'es-ES', 'fr-FR')
 */
export const getUserLocale = () => {
  // Try to get locale from browser's navigator object
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  // Fallback to navigator.languages if available
  if (typeof navigator !== 'undefined' && navigator.languages && navigator.languages.length > 0) {
    return navigator.languages[0];
  }
  // Final fallback to en-US
  return 'en-US';
};

/**
 * Common formatting functions for different field types
 */
export const formatFunctions = {
  // Currency formatting using Intl.NumberFormat
  currency: (value, callbackArguments = {}) => {
    if (value === null || value === undefined || value === '') return '';
    const numValue = Number(value);
    if (isNaN(numValue)) return value;

    const { currencyCode = 'USD', locale = 'en-US' } = callbackArguments;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode
    }).format(numValue);
  },

  type: (value) => {
    if (!value || value.trim() === '') return null;
    return '(' + value + ')';
  },

  // Plain text (no formatting)
  text: (value) => value || '',
};

/**
 * Common parsing functions for different field types
 */
export const parseFunctions = {
  // Currency parsing - converts formatted currency strings back to numbers
  currency: (value) => {
    if (!value || value.trim() === '') return null;
    // Remove currency symbols, commas, spaces, and other non-numeric characters except decimal point
    const cleanValue = value.replace(/[^\d.-]/g, '');
    const parsed = parseFloat(cleanValue);
    if (isNaN(parsed)) {
      throw new Error(`Invalid currency format: ${value}`);
    }
    return parsed;
  },

  type: (value) => {
    if (!value || value.trim() === '') return null;
    value = value.replace(/^\(|\)$/g, '');
    return value;
  },

  // Plain text parsing (no conversion)
  text: (value) => value,
};

/**
 * Gets the appropriate formatting function for a field
 * @param {string} fieldName - Name of the field
 * @returns {Function} Formatting function
 */
export const getFormatFunction = (fieldName) => {
  // Currency fields
  if (fieldName.includes('total_') || fieldName.includes('charge') || fieldName.includes('balance') || fieldName.includes('paid') || fieldName.includes('adjustment')) {
    return formatFunctions.currency;
  }

  if (fieldName.includes('_type')) {
    return formatFunctions.type;
  }

  // Default to text
  return formatFunctions.text;
};

/**
 * Gets the appropriate parsing function for a field
 * @param {string} fieldName - Name of the field
 * @returns {Function} Parsing function
 */
export const getParseFunction = (fieldName) => {
  // Currency fields
  if (fieldName.includes('total_') || fieldName.includes('charge') || fieldName.includes('balance') || fieldName.includes('paid') || fieldName.includes('adjustment')) {
    return parseFunctions.currency;
  }

  // Default to text
  return parseFunctions.text;
};

/**
 * Helper function to find the index of an item in an array by its ID
 * @param {Array} items - Array of items
 * @param {string} itemId - ID to search for
 * @returns {number} Index of the item, or -1 if not found
 */
export const findItemIndex = (items, itemId) => {
  return items.findIndex(item => item.id === itemId);
};

/**
 * Helper function to get the document name from an item
 * @param {Object} item - Item object
 * @returns {string} Document name
 */
export const getDocumentName = (item) => {
  return item.source_filename || item.filename || '';
};

/**
 * Converts a filename to a JSONPath-compatible document key
 * Characters that are not allowed in JSONPath keys are removed: [ ] ^ ;
 * @param {string} key - The filename to convert
 * @returns {string} JSONPath-compatible key
 */
export const getJSONPathCompatibleKey = (key) => {
  // eslint-disable-next-line no-useless-escape
  return key.replace(/[\[\]^;]/g, '');
};


/**
* Gets list of unconsolidated files for the given extraction analysis ids, extraction keys, and consolidation analysis id. Use to get data that has been extracted from documents but has not been consolidated yet.
* @param {*} caseId
* @param {*} extractionAnalysisIds list of analysis ids for the extraction analysis
* @param {*} extractionKeys keys of the extractions to check for
* @param {*} consolidationAnalysisId analysis id for the consolidation analysis
* @returns object that contains extractions from all documents concatenated into a single array for each response key with filename added to the source field
*/
export const getFilesWithUnConsolidatedExtractions = (caseInfo, extractionAnalysisIds, extractionKeys, consolidationAnalysisId) => {
  var filesWithUnConsolidatedExtractions = [];

  extractionAnalysisIds.forEach(extractionAnalysisId => {
    for (const docKey in caseInfo['documents']) {
      const docInfo = caseInfo['documents'][docKey];
      const docAnalysis = docInfo['analysis'];
      if ('analysis_history' in docInfo &&
        docInfo['analysis_history'].lastIndexOf(extractionAnalysisId) > docInfo['analysis_history'].lastIndexOf(consolidationAnalysisId) &&
        extractionKeys.some(key => docAnalysis && key in docAnalysis && docAnalysis[key].length > 0)) {
        filesWithUnConsolidatedExtractions.push(docKey);
      }
    }
  });
  return filesWithUnConsolidatedExtractions;
}

export const hasConsolidatedExtractions = (caseInfo, extractionAnalysisId, consolidationAnalysisId) => {
  for (const docKey in caseInfo['documents']) {
    const docInfo = caseInfo['documents'][docKey];
    if ('analysis_history' in docInfo) {
      const extractionIdx = docInfo['analysis_history'].lastIndexOf(extractionAnalysisId);
      const consolidationIdx = docInfo['analysis_history'].lastIndexOf(consolidationAnalysisId);
      if (extractionIdx >= 0 && consolidationIdx >= 0 && extractionIdx < consolidationIdx) {
        return true;
      }
    }
  }
  return false;
};

/** Analysis ids that can produce medical_chronology / medical_expenses for consolidation. */
export const MEDICAL_EXTRACTION_ANALYSIS_IDS = ['medical_records', 'personal_injury_catchall', 'discovery_evidence'];

export const hasAnyConsolidatedExtractions = (caseInfo, consolidationAnalysisId) =>
  MEDICAL_EXTRACTION_ANALYSIS_IDS.some((id) =>
    hasConsolidatedExtractions(caseInfo, id, consolidationAnalysisId)
  );

export const getMedicalChronologyConsolidationState = (caseInfo) => {
  const unconsolidatedFiles = getFilesWithUnConsolidatedExtractions(
    caseInfo,
    MEDICAL_EXTRACTION_ANALYSIS_IDS,
    ['medical_chronology'],
    'medical_reports_canonicalize'
  );
  const hasConsolidated = hasAnyConsolidatedExtractions(caseInfo, 'medical_reports_canonicalize');
  const hasUnconsolidated = unconsolidatedFiles.length > 0;
  const showConsolidateButton = (hasConsolidated && hasUnconsolidated) || unconsolidatedFiles.length > 1;
  return { hasConsolidated, unconsolidatedFiles, hasUnconsolidated, showConsolidateButton };
};

/** discovery_evidence with legacy analysis.discovery fallback. */
export const getDiscoveryEvidenceFromAnalysis = (analysis) => {
  if (!analysis || typeof analysis !== 'object') return [];
  const fromNew = analysis.discovery_evidence;
  if (Array.isArray(fromNew) && fromNew.length > 0) return fromNew;
  const fromLegacy = analysis.discovery;
  return Array.isArray(fromLegacy) ? fromLegacy : [];
};

export const caseHasDiscoveryEvidence = (caseInfo) => {
  if (!caseInfo?.documents) return false;
  return Object.values(caseInfo.documents).some(
    (doc) => getDiscoveryEvidenceFromAnalysis(doc.analysis).length > 0
  );
};

/** Reason from needs_review/caveats array entry matching field_name. */
export const getWarningReasonForField = (warnings, fieldName) => {
  if (!Array.isArray(warnings)) return null;
  const entry = warnings.find((w) => w?.field_name === fieldName);
  return entry?.reason ?? null;
};

export const buildDiscoveryEvidencePaths = (documentName, itemIndex, fieldName) => {
  const base = `$.documents['${documentName}'].analysis.discovery_evidence[${itemIndex}]`;
  return {
    textPath: `${base}.${fieldName}`,
    needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
    caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats'),
  };
};

/** Minutes from midnight for 24-hour HH:mm; 0 if missing or invalid. */
export function parseTreatmentTimeToMinutes(t) {
  if (t == null || t === '') return 0;
  const s = typeof t === 'string' ? t : String(t);
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return 0;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h > 23 || min > 59) return 0;
  return h * 60 + min;
}

/**
 * Milliseconds for sorting chronology rows by treatment start date plus optional treatment_start_time.
 * @returns {number} NaN if no parseable start date
 */
export function chronologyTreatmentStartSortMs(item) {
  const dStr = item?.treatment_start_date || '';
  const dayMs = Date.parse(dStr);
  if (Number.isNaN(dayMs)) return NaN;
  return dayMs + parseTreatmentTimeToMinutes(item?.treatment_start_time) * 60 * 1000;
}

/**
 * Renders treatment dates as JSX elements
 * @param {Object} caseInfo - Case information object
 * @param {string} baseFieldPath - JSONPath to the object containing the dates (e.g., "$.documents['file.pdf'].analysis.medical_expenses[0]")
 * @param {Function} setSnackBarMessage - Function to set snackbar messages
 * @param {Function} setDisplayedDocument - Function to set displayed document (optional, for non-consolidated views)
 * @param {Function} getFormatFunction - Function to get format function for dates (optional)
 * @param {Object} components - React components object containing Box, Typography, and EditableFieldWithWarnings
 * @returns {JSX.Element} Rendered treatment dates component
 */
export const renderTreatmentDates = (caseInfo, setCaseInfo, baseFieldPath, setSnackBarMessage, setDisplayedDocument, documentKey) => {
  // Get the item data using the base field path
  const itemResults = JSONPath({
    json: caseInfo,
    path: baseFieldPath,
    resultType: 'value'
  });

  if (itemResults.length === 0) {
    return '';
  }

  const item = itemResults[0];
  const startDate = item.treatment_start_date;
  const endDate = item.treatment_end_date;

  const getFieldPaths = (fieldName) => ({
    textPath: `${baseFieldPath}.${fieldName}`,
    needsReviewPath: buildArraySchemaWarningPath(baseFieldPath, fieldName, 'needs_review'),
    caveatsPath: buildArraySchemaWarningPath(baseFieldPath, fieldName, 'caveats')
  });

  let source = item.source || item.sources?.[0] || {};
  const getWarningClickHandler = () => {
    return (e) => {
      e.stopPropagation();

      // Prefer the known document key in the document-based view. Many `source`
      // objects only include `page`/`bates_number` and no `filename`.
      const resolvedDocumentKey = documentKey
        ? getJSONPathCompatibleKey(documentKey)
        : getJSONPathCompatibleKey(source?.filename || '');

      setDisplayedDocument({
        'key': resolvedDocumentKey,
        'page': source?.page || 1
      });
    };
  };

  const warningClickHandler = getWarningClickHandler();

  const showStartTime = item.treatment_start_time != null && item.treatment_start_time !== '';
  const showEndTime = item.treatment_end_time != null && item.treatment_end_time !== '';

  const startTimeField = showStartTime ? (
    <>
      {' '}
      <EditableFieldWithWarnings
        onWarningClick={warningClickHandler}
        caseInfo={caseInfo}
        setCaseInfo={setCaseInfo}
        {...getFieldPaths('treatment_start_time')}
        setSnackBarMessage={setSnackBarMessage}
      />
    </>
  ) : null;

  const endTimeField = showEndTime ? (
    <>
      {' '}
      <EditableFieldWithWarnings
        onWarningClick={warningClickHandler}
        caseInfo={caseInfo}
        setCaseInfo={setCaseInfo}
        {...getFieldPaths('treatment_end_time')}
        setSnackBarMessage={setSnackBarMessage}
      />
    </>
  ) : null;

  // Determine which date fields to render and return JSX directly
  if (startDate && endDate && startDate !== endDate) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 0.5, rowGap: 0.5 }}>
        <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center' }}>
          <EditableFieldWithWarnings
            onWarningClick={warningClickHandler}
            caseInfo={caseInfo}
            setCaseInfo={setCaseInfo}
            {...getFieldPaths('treatment_start_date')}
            setSnackBarMessage={setSnackBarMessage}
          />
          {startTimeField}
        </Box>
        <Typography component="span" sx={{ mx: 1 }}>
          -
        </Typography>
        <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center' }}>
          <EditableFieldWithWarnings
            onWarningClick={warningClickHandler}
            caseInfo={caseInfo}
            setCaseInfo={setCaseInfo}
            {...getFieldPaths('treatment_end_date')}
            setSnackBarMessage={setSnackBarMessage}
          />
          {endTimeField}
        </Box>
      </Box>
    );
  } else if (startDate) {
    const sameCalendarDay = endDate && String(startDate) === String(endDate);
    return (
      <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 0.5, rowGap: 0.5 }}>
        <EditableFieldWithWarnings
          onWarningClick={warningClickHandler}
          caseInfo={caseInfo}
          setCaseInfo={setCaseInfo}
          {...getFieldPaths('treatment_start_date')}
          setSnackBarMessage={setSnackBarMessage}
        />
        {startTimeField}
        {sameCalendarDay &&
          showEndTime &&
          (!showStartTime || item.treatment_start_time !== item.treatment_end_time) ? (
          <>
            <Typography component="span" sx={{ mx: 0.5 }}>
              –
            </Typography>
            <EditableFieldWithWarnings
              onWarningClick={warningClickHandler}
              caseInfo={caseInfo}
              setCaseInfo={setCaseInfo}
              {...getFieldPaths('treatment_end_time')}
              setSnackBarMessage={setSnackBarMessage}
            />
          </>
        ) : null}
      </Box>
    );
  }

  return '';
};
